import P from '@hero/ui-kit/typography/P';
import React from 'react';
import * as Style from './style.module.scss';
import PlusIcon from '@hero/ui-kit/icons/utility/PlusIcon';
import getApprovalStatus from '../utils/getApprovalStatus';
import NewActivityForm from '../components/NewActivityForm';
import Button from '@hero/ui-kit/inputs/Button';
import {
    ActivityLogEntry,
    AllApprovalStatusSubStates,
    ApprovalStatus,
    LeadActivityDetail,
    LeadDetailsResponse,
    LeadStateResponse
} from '../types';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import MedicareLeadForm from '../components/EnrolmentForm';
import RejectFormFillModal from '../components/modals/RejectFormFillModal';
import MedicalCheckSchedulingModal from '../components/modals/MedicalCheckSchedulingModal';
import { getMedicalCheckStatus } from '../utils/medicalCheckStatus';
import LeadActivity from './LeadActivity';
import { useLeadDetailsContext } from './v2/context/LeadDetailsContext';
import { format } from 'date-fns';
import RefreshIcon from '../../../icons/RefreshIcon';
import ExpiredLeadActivity from './ExpiredLeadActivity';
import Section from '@hero/ui-kit/layout/Section';

const StatusDivider: React.FC<{ status: string }> = ({ status }) => {
    return (
        <span className={Style.statusDivider}>
            <h2>
                <span>{status}</span>
            </h2>
        </span>
    );
};

// HWM-22633: the four CRM action cards (Medical check, Communication, Call
// Disposition, SLA Timer) live on LeadContactDetails (left rail) per Figma
// 5274:4285. This file keeps only the header + Notes + activity feed.
//
// HWM-22633 round 4b: status_change rendering reverted to the legacy
// `LeadActivity` per-row visual (StatusDivider + agent/timestamp + bold
// sub-status + per-row note CRUD attached below each item). Driven from
// `activity_log[kind=status_change]` (round-4 backend emit) so each row
// carries the historical PatientLead PK needed by the note endpoint.

// HWM-22633 round 4b: extended LeadActivityDetail with the historical
// PatientLead PK so `LeadActivity` can wire its inline note CRUD to
// `PATCH /enterprise/leads/<id>/status-changes/<historical_id>/note/`.
type StatusChangeActivity = LeadActivityDetail & { historicalId: number };

interface LeadActivitiesProps {
    lead?: LeadDetailsResponse;
    leadState?: LeadStateResponse;
    isLocked?: boolean;
    refetchLeadDetails?: () => void;
    refetchLeadState?: () => void;
    leadId: string;
    canEdit?: boolean;
}

const LeadActivities: React.FC<LeadActivitiesProps> = ({
    lead,
    leadId,
    isLocked = false,
    refetchLeadDetails,
    refetchLeadState,
    leadState,
    canEdit = true
}) => {
    const [showModal, setShowModal] = React.useState(false);
    const [showEnrollModal, setShowEnrollModal] = React.useState(false);
    const [showRejectModal, setShowRejectModal] = React.useState(false);
    const [showConfirmReactivateModal, setShowConfirmReactivateModal] = React.useState(false);
    // HWM-22859: "Change status → Medical check" routes to the medical-check
    // scheduling modal (the wrapper picks manual vs DrChrono UI) instead of a
    // bare, date-less status change.
    const [showMcModal, setShowMcModal] = React.useState(false);
    const [isReschedulingMc, setIsReschedulingMc] = React.useState(false);
    const { onBlockNext } = useLeadDetailsContext();

    const handleClick = () => {
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleCloseEnrollModal = () => {
        setShowEnrollModal(false);
    };

    const handleOpenEnrollModal = () => {
        setShowEnrollModal(true);
    };

    const handleCloseRejectModal = () => {
        setShowRejectModal(false);
    };

    const handleOpenRejectModal = () => {
        setShowRejectModal(true);
    };

    const handleCloseConfirmReactivateModal = () => {
        setShowConfirmReactivateModal(false);
    };

    const handleOpenConfirmReactivateModal = () => {
        setShowConfirmReactivateModal(true);
    };

    // HWM-22633 round 4b: drive the activity timeline from
    // `activity_log[kind=status_change]` (round-4 backend) and adapt each
    // entry to the legacy `LeadActivityDetail` shape so we can reuse the
    // existing `LeadActivity` visual + group-by-status divider layout.
    // `historicalId` is the new field — the PatientLead row PK — passed
    // down so `LeadActivity` can wire its inline note CRUD to
    // `useUpdateStatusChangeNote`.
    const statusChangeActivities = React.useMemo<StatusChangeActivity[]>(() => {
        // Filter out malformed entries (no to_status) — they'd render as
        // "Not Found" in the StatusDivider via getApprovalStatus(""). The
        // backend always emits a to_status for valid status_change rows;
        // a falsy value here is data corruption, not a legitimate state.
        const entries = (lead?.activity_log ?? []).filter(
            (e): e is Extract<ActivityLogEntry, { kind: 'status_change' }> =>
                e.kind === 'status_change' && Boolean(e.to_status)
        );
        return entries.map((entry) => ({
            historicalId: entry.id,
            // the filter above guarantees a truthy to_status; the activity log types both
            // fields as bare strings while the timeline needs the narrower unions
            status: entry.to_status as ApprovalStatus,
            sub_status: (entry.to_sub_status ?? null) as AllApprovalStatusSubStates | null,
            updated_by: entry.updated_by ?? '',
            updated_at: entry.created_at,
            note: entry.note ?? null,
            note_datetime: entry.note_date_time ?? null
        }));
    }, [lead?.activity_log]);

    const groupedActivities = React.useMemo(() => {
        return statusChangeActivities
            .slice()
            .sort((a, b) => {
                const aValue = new Date(a.updated_at).valueOf();
                const bValue = new Date(b.updated_at).valueOf();
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            })
            .reduce<Record<string, StatusChangeActivity[]>>((group, activity) => {
                const status = (activity.status ?? '').toUpperCase();
                const date = format(new Date(activity.updated_at), 'yyyy-MM-dd HH');
                const key = `${status}-${date}`;
                group[key] = group[key] ?? [];
                group[key].push(activity);
                return group;
            }, {});
    }, [statusChangeActivities]);

    const stageBtn = React.useMemo(() => {
        const isAlreadyReactivated =
            ['REJECTED', 'EXPIRED'].includes(lead?.status?.toUpperCase() || '') &&
            lead?.primary_user_details.reactivated_date;

        if (
            !canEdit ||
            ['ENROLLED'].includes(lead?.sub_status?.toUpperCase() || '') ||
            isAlreadyReactivated
        ) {
            return <div />;
        }

        if (
            ['REJECTED', 'EXPIRED'].includes(lead?.status?.toUpperCase() || '') &&
            !lead?.primary_user_details.reactivated_date
        ) {
            return (
                <Button
                    variant="outlineSecondary"
                    styles={{ minHeight: '4rem' }}
                    onClick={handleOpenConfirmReactivateModal}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            fontSize: '1.6rem'
                        }}
                    >
                        <RefreshIcon size="utility" type="utility" width={20} height={20} />
                        <strong>Reactivate</strong>
                    </div>
                </Button>
            );
        }

        // HWM-22633: "Change status" opens the existing NewActivityForm
        // (which already handles status changes to qualified / approved /
        // medical_check etc., plus date/time inputs for medical-check
        // scheduling on non-DrChrono providers). Always visible alongside
        // any contextual Reject/Enroll buttons.
        const changeStatusBtn = (
            <Button
                onClick={handleClick}
                styles={{
                    height: '4rem',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 2rem'
                }}
                disabled={isLocked}
                variant="outlineSecondary"
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        fontSize: '1.6rem'
                    }}
                >
                    <PlusIcon size="utility" width={20} height={20} type="utility" />
                    <strong>Change status</strong>
                </div>
            </Button>
        );

        if (lead?.status?.toUpperCase() === 'APPROVED') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '1rem' }}>
                    {changeStatusBtn}
                    <Button
                        onClick={handleClick}
                        styles={{
                            height: '4rem',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 2rem'
                        }}
                        disabled={isLocked}
                        variant="outlineSecondary"
                    >
                        Reject
                    </Button>
                    <Button
                        styles={{
                            height: '4rem',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 2rem'
                        }}
                        onClick={handleOpenEnrollModal}
                        disabled={isLocked}
                    >
                        Enroll
                    </Button>
                </div>
            );
        }

        return changeStatusBtn;
    }, [lead, isLocked]);

    const handleActivitySuccess = () => {
        refetchLeadDetails && refetchLeadDetails();
        refetchLeadState && refetchLeadState();
        handleModalClose();
        handleCloseEnrollModal();
        onBlockNext();
    };

    const handleLeadEnrollSuccess = () => {
        handleModalClose();
        handleCloseEnrollModal();
        refetchLeadDetails && refetchLeadDetails();
    };

    const handleRejectSuccess = () => {
        handleCloseRejectModal();
        refetchLeadDetails && refetchLeadDetails();
        refetchLeadState && refetchLeadState();
        onBlockNext();
    };

    const handleSuccessEnrollModal = () => {
        refetchLeadDetails && refetchLeadDetails();
        refetchLeadState && refetchLeadState();
    };

    // HWM-22859: route a Medical-check selection from the New Activity form to
    // the medical-check scheduling modal (the wrapper picks manual vs DrChrono
    // UI based on the org's provider).
    const handleMedicalCheckSelected = () => {
        const mcStatus = getMedicalCheckStatus(lead);
        setIsReschedulingMc(mcStatus === 'scheduled' || mcStatus === 'missed');
        handleModalClose();
        setShowMcModal(true);
    };

    const handleCloseMcModal = () => {
        setShowMcModal(false);
        setIsReschedulingMc(false);
    };

    const handleMcModalSuccess = () => {
        setShowMcModal(false);
        refetchLeadDetails && refetchLeadDetails();
        refetchLeadState && refetchLeadState();
        onBlockNext();
    };

    const groupedEntries = React.useMemo(
        () => Object.entries(groupedActivities),
        [groupedActivities]
    );

    return (
        <>
            <div className={Style.activitiesHeader}>
                <P noDefaultMargin strong size="large">
                    Activities
                </P>
                {stageBtn}
            </div>

            {/* HWM-22633 round 4b: legacy `LeadActivity` per-status visual,
                    grouped by StatusDivider. Each row is now driven from
                    `activity_log[kind=status_change]` so it carries the
                    historical PatientLead PK needed by the per-row note
                    CRUD (NOTE block + Edit/Delete or "Add a note" pill). */}
            {groupedEntries.map((groupedActivity, index) => {
                const groupActivities = groupedActivity[1] || [];
                const statusActivity = groupedActivity[0];
                const statusApproval = statusActivity.split('-')[0];

                const nextIndex = index + 1;
                const previousActivityStatus = groupedEntries[nextIndex]
                    ? groupedEntries[nextIndex][0]
                    : '';
                const previousStatus = previousActivityStatus.split('-')[0];

                return (
                    <React.Fragment key={`${statusActivity}-${index}`}>
                        <StatusDivider
                            status={getApprovalStatus(statusApproval as ApprovalStatus).label}
                        />
                        {groupActivities.map((activity, idx) => {
                            return (activity.status ?? '').toLowerCase() === 'expired' ? (
                                <ExpiredLeadActivity
                                    key={`${activity.status}-${activity.sub_status}-${idx}`}
                                    activityDetails={activity}
                                    isExpiredCurrentStatus={
                                        lead?.status?.toUpperCase() === 'EXPIRED'
                                    }
                                />
                            ) : (
                                <LeadActivity
                                    key={`${activity.status}-${activity.sub_status}-${idx}`}
                                    activityDetails={activity}
                                    leadId={leadId}
                                    canEdit={canEdit && !isLocked}
                                    isReactivated={
                                        previousStatus === 'REJECTED' &&
                                        idx === groupActivities.length - 1
                                    }
                                />
                            );
                        })}
                    </React.Fragment>
                );
            })}

            {showModal ? (
                <NewActivityForm
                    leadName={`${lead?.primary_user_details.first_name} ${lead?.primary_user_details.last_name}`}
                    handleModalClose={handleModalClose}
                    state={leadState}
                    leadId={leadId}
                    onActivitySuccess={handleActivitySuccess}
                    onEnrollSelected={() => {
                        handleModalClose();
                        handleOpenEnrollModal();
                    }}
                    onRejectSelected={() => {
                        handleModalClose();
                        handleOpenRejectModal();
                    }}
                    onMedicalCheckSelected={handleMedicalCheckSelected}
                />
            ) : null}

            {showMcModal && (
                <MedicalCheckSchedulingModal
                    lead={lead}
                    leadId={leadId}
                    isReschedule={isReschedulingMc}
                    onClose={handleCloseMcModal}
                    onSuccess={handleMcModalSuccess}
                />
            )}

            <Modal
                externalControls={[showRejectModal, setShowRejectModal]}
                isCancelable
                onClose={handleCloseRejectModal}
                styles={{ padding: '0', width: '60rem' }}
            >
                <RejectFormFillModal
                    leadId={leadId}
                    leadName={`${lead?.primary_user_details.first_name} ${lead?.primary_user_details.last_name}`}
                    onSuccess={handleRejectSuccess}
                    onCancel={handleCloseRejectModal}
                />
            </Modal>

            <Modal
                externalControls={[showEnrollModal, setShowEnrollModal]}
                isCancelable
                onClose={handleCloseEnrollModal}
                styles={{ padding: '0' }}
            >
                <H role="h5">Enroll Form fill</H>
                <MedicareLeadForm
                    leadId={leadId}
                    firstName={
                        lead?.primary_user_details.first_name || lead?.contact_details.first_name
                    }
                    lastName={
                        lead?.primary_user_details.last_name || lead?.contact_details.last_name
                    }
                    isCaregiver={!!lead?.contact_details.first_name}
                    email={lead?.primary_user_details.email}
                    address_line_1={lead?.address_details.street_address}
                    address_line_2={lead?.address_details.apartment}
                    state={lead?.address_details.state}
                    city={lead?.address_details.city}
                    zip_code={lead?.address_details.shipping_zip}
                    phone={lead?.primary_user_details.phone}
                    isMember={lead?.is_existing_member}
                    admin_email={lead?.primary_user_details.admin_email}
                    external_serial={lead?.primary_user_details.external_serial}
                    external_user_id={lead?.primary_user_details.external_user_id}
                    formVersion={lead?.form_version}
                    onSuccess={handleLeadEnrollSuccess}
                    onSuccessModal={handleSuccessEnrollModal}
                />
            </Modal>
            <Modal
                externalControls={[showConfirmReactivateModal, setShowConfirmReactivateModal]}
                isCancelable
                onClose={handleCloseConfirmReactivateModal}
                styles={{ padding: '0', width: '60rem' }}
            >
                <>
                    <P
                        strong
                        centered
                        noDefaultMargin
                        styles={{ marginBottom: '2.4rem', fontSize: '3.2rem', marginTop: '2rem' }}
                    >
                        Reactivate application
                    </P>
                    <P centered noDefaultMargin styles={{ marginBottom: '3.6rem' }}>
                        <strong>Enrollment process must be completed within 21 days</strong>,<br />{' '}
                        or application expires permanently.
                    </P>
                    <Section centered noDefaultPadding>
                        <div style={{ display: 'flex', alignItems: 'center', columnGap: '2.4rem' }}>
                            <Button
                                variant="outlineSecondary"
                                onClick={handleCloseConfirmReactivateModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowConfirmReactivateModal(false);
                                    handleClick();
                                }}
                            >
                                Continue
                            </Button>
                        </div>
                    </Section>
                </>
            </Modal>
        </>
    );
};

export default LeadActivities;
